import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import debounce from 'lodash.debounce';
import { Config } from '@leverege/plugin';
import { Attributes, DataSources } from '@leverege/ui-attributes';
import { TitleBar } from '@leverege/ui-plugin';
import { Button, CompositeTextInput, Content, Flex, List, Pane, Text } from '@leverege/ui-elements';
import { GlobalState } from '@leverege/ui-redux';
import { PuffLoader } from 'react-spinners';
import { isQR, parseQR } from '../../util/QR';
import { getCompanyAndLocationIdsFromUrl } from '../../util/Url';
import S from './DeviceSearch.css';
import { isVIN as isValidVIN, ignoreNonVINCharacterEvent, removeNonVINCharacters } from '../../util/VIN';
import { testConditions } from '../../util/Condition';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default function DeviceSearch(props) {
  const {
    value,
    onClick,
    onChange,
    eventData,
    actions,
    parentNetworkId,
    networkId,
    titleVariant,
    titleIcon,
    onClose,
    disabledConditions = [],
    location,
    additionalFilters = [],
    iFace,
    itemName: pItemName,
    network: pNetwork,
    pairing = true
  } = props;
  const [val, setVal] = useState(value);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const isVin = eventData === 'assetId';
  const itemName = pItemName || isVin ? 'VIN' : 'Locator';
  let nwkId;
  if (isVin) {
    nwkId = parentNetworkId;
  } else {
    nwkId = networkId;
  }
  const network = pNetwork || isVin ? 'VehicleNetwork.dealer' : 'LocatorNetwork';
  const aliasKey = Config.get(network, 'aliasKey', 'serialNumber');
  const onAddVehicle = useCallback(() => {
    onChange({
      data: eventData,
      value: val
    });
    onClick({
      data: {
        screen: 'add',
        field: eventData
      }
    });
  }, [onClick, val, onChange, eventData]);
  const onSearch = ({
    value
  }) => {
    const newVal = isVin ? removeNonVINCharacters(value?.trim().toUpperCase()).slice(0, 17) : value?.trim();
    setVal(newVal);
  };
  const onClear = () => {
    setVal(null);
  };
  const onSelect = useCallback(async ({
    data
  }) => {
    const netId = data?.networkAliases?.[nwkId]?.[aliasKey];
    onSearch({
      value: null
    });
    onChange({
      data: eventData,
      value: netId,
      object: data
    });
  }, [onChange, nwkId, aliasKey, eventData, onSearch]);
  const searchIsValid = val && val.length > 2;
  const debounceParse = debounce(() => {
    if (val && isQR(val)) {
      setVal(parseQR(val));
    }
  }, 500);
  useEffect(debounceParse, [val]);
  const actionId = useRef();
  useEffect(() => {
    if (searchIsValid) {
      const actionsUrl = actions.getInterfaceUrl();
      let {
        companyId
      } = getCompanyAndLocationIdsFromUrl(actionsUrl);
      let deviceType, deviceId, ref;
      if (pairing) {
        if (actionsUrl.includes('company')) {
          deviceType = 'company';
          // truncates the url so that the end is "company"
          ref = truncateUrl(actionsUrl, 'company');
          deviceId = companyId;
        } else if (actionsUrl.includes('location')) {
          companyId = location?.data?.data?.company?.id;
          deviceType = companyId ? 'company' : 'location';
          // trunctions the url so that the end is "locations"
          ref = truncateUrl(actionsUrl, 'locations');
          ref = companyId ? ref.replace('location', 'company') : ref;
          deviceId = companyId || location.id;
        }
      } else {
        deviceType = 'location';
        deviceId = location.id;
        ref = truncateUrl(actionsUrl, 'locations');
      }
      const deviceChildrenPath = isVin ? 'vehicles' : 'locators';
      const delegate = DataSources.getActions({
        id: deviceId,
        type: deviceType,
        ref
      });
      const iFaceObj = iFace || delegate.child(deviceChildrenPath);
      const nActionId = Date.now();
      actionId.current = nActionId;
      const action = iFaceObj.search({
        limit: 100,
        filter: {
          type: 'logical',
          operator: 'and',
          conditions: [{
            type: 'expression',
            value: `*${val.toLowerCase()}*`,
            field: `networkAliases/${nwkId}/${aliasKey}`,
            kind: 'queryString',
            analyzeWildcard: true,
            ignoreCase: true
          }, ...additionalFilters]
        }
      }, null, 'pairingSearch');
      setItems([]);
      setLoading(true);
      GlobalState.dispatch(action).then(res => {
        if (nActionId === actionId.current) {
          setItems(res.items);
          setLoading(false);
        }
      }).catch(err => {
        console.error(err);
        Sentry.captureException(err);
      });
    } else {
      setItems([]);
      setLoading(false);
    }
  }, [val, iFace]);
  const onKeyDown = evt => isVin && ignoreNonVINCharacterEvent(evt);
  return /*#__PURE__*/_jsxs(React.Fragment, {
    children: [/*#__PURE__*/_jsxs(Content.Header, {
      layout: "flex:colL",
      children: [/*#__PURE__*/_jsxs("div", {
        style: {
          position: 'relative'
        },
        children: [/*#__PURE__*/_jsx(TitleBar, {
          variant: titleVariant,
          title: `Search ${itemName}`,
          icon: titleIcon
        }), /*#__PURE__*/_jsx(Button, {
          variant: "pairClose",
          className: S.close,
          onClick: onClose
        })]
      }), /*#__PURE__*/_jsxs(Pane, {
        variant: titleVariant,
        children: [/*#__PURE__*/_jsx(CompositeTextInput, {
          autoFocus: true,
          "data-cy": Config.get('cypress', ['input', 'device.search'], 'input.device.search'),
          onFocus: () => {
            setTimeout(() => window?.scrollTo(0, 0), 250);
          },
          pre: /*#__PURE__*/_jsx(Button, {
            variant: "searchBack",
            eventData: {
              screen: 'main',
              field: null
            },
            onClick: onClick
          }),
          post: val && val.length > 0 && /*#__PURE__*/_jsx(Button, {
            variant: "pairClose",
            onClick: onClear
          }),
          variant: "fullWidth",
          value: val,
          onChange: onSearch,
          onKeyDown: onKeyDown,
          changeOnReturn: false,
          alwaysFireOnReturn: false
        }), items && items.length > 0 && /*#__PURE__*/_jsx(Text, {
          variant: "caption",
          className: S.resultCount,
          children: `${items.length} results`
        }), loading && /*#__PURE__*/_jsx(Text, {
          variant: "caption",
          className: S.resultCount,
          children: "Searching..."
        }), searchIsValid && !loading && items.length < 1 && /*#__PURE__*/_jsx(Text, {
          variant: "caption",
          className: S.resultCount,
          children: "0 results"
        })]
      })]
    }), /*#__PURE__*/_jsx(Content.Area, {
      className: S.list,
      children: /*#__PURE__*/_jsxs(Flex, {
        variant: "colL",
        children: [items && items.length > 0 ? /*#__PURE__*/_jsxs(React.Fragment, {
          children: [/*#__PURE__*/_jsx(Text, {
            variant: "subtitle",
            children: "Suggestions"
          }), /*#__PURE__*/_jsx(List, {
            variant: "pairingItem",
            onClick: onSelect,
            children: items.sort(item => {
              const disabled = testConditions(disabledConditions, item);
              if (disabled) {
                return 1;
              }
              return -1;
            }).map(item => {
              return /*#__PURE__*/_jsx(ListItem, {
                networkId: nwkId,
                aliasKey: aliasKey,
                item: item,
                disabledConditions: disabledConditions
              }, item.id);
            })
          }), isVin && items.length > 1 && /*#__PURE__*/_jsx(Button, {
            variant: "addVehicleLink",
            onClick: onAddVehicle,
            children: "Add Vehicle to System"
          })]
        }) : searchIsValid ? /*#__PURE__*/_jsxs(React.Fragment, {
          children: [loading ? /*#__PURE__*/_jsx(Flex, {
            direction: "column",
            justify: "center",
            align: "center",
            children: /*#__PURE__*/_jsx(PuffLoader, {
              size: 48
            })
          }) : /*#__PURE__*/_jsxs(Text, {
            children: ["A ", itemName, " matching ", val, " does not exist in the system."]
          }), isVin && !isValidVIN(val) && /*#__PURE__*/_jsx(Text, {
            variant: "error",
            children: "This VIN does not follow the standard formatting of 17 characters"
          })]
        }) : /*#__PURE__*/_jsx(Text, {
          children: "Enter at least 3 characters to search."
        }), isVin && searchIsValid && items.length === 0 && /*#__PURE__*/_jsx(Button, {
          variant: "addVehicle",
          onClick: onAddVehicle,
          children: "Add Vehicle to System"
        })]
      })
    })]
  });
}
function ListItem(props) {
  const {
    item,
    model,
    networkId,
    aliasKey,
    disabledConditions,
    ...rest
  } = props;
  const disabled = testConditions(disabledConditions, item);
  return /*#__PURE__*/_jsx(List.Item, {
    "data-test-id": `${item.id}`,
    disabled: disabled && disabled.disable !== false,
    className: S.suggestion,
    item: item,
    ...rest,
    children: /*#__PURE__*/_jsxs(Flex, {
      variant: "rowM",
      align: "center",
      children: [/*#__PURE__*/_jsx(Text, {
        icon: "@icon:search"
      }), /*#__PURE__*/_jsxs(Flex, {
        direction: "column",
        children: [/*#__PURE__*/_jsx(Text, {
          variant: "pairingSuggestion",
          children: item?.networkAliases?.[networkId]?.[aliasKey]
        }), disabled && disabled.messages.map(message => /*#__PURE__*/_jsx(Text, {
          variant: "pairingSuggestionSubtitle",
          children: message
        }, message))]
      })]
    })
  });
}

// removes all characters after the word locations or location, but not including, to create a ref for search
function truncateUrl(url, blueprint) {
  let strMatch;
  if (blueprint === 'company') {
    strMatch = url.match(/company/);
  } else {
    strMatch = url.match(/location(s?)/);
  }
  return `${url.split(strMatch[0])[0]}${strMatch[0]}`;
}